import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Upload,
  Modal,
  Input,
  Select,
  Tag,
  message,
  Popconfirm,
  Image,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import * as fileService from '../services/fileService';

const FileManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<fileService.FileRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchName, setSearchName] = useState('');
  const [searchExt, setSearchExt] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [stats, setStats] = useState<{ totalCount: number; totalSize: number; typeStats: any[] }>({
    totalCount: 0,
    totalSize: 0,
    typeStats: [],
  });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const loadFiles = async () => {
    setLoading(true);
    try {
      const res = await fileService.listFiles({
        page,
        pageSize,
        name: searchName || undefined,
        ext: searchExt || undefined,
      });
      setFiles(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载文件列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fileService.getFileStats();
      setStats(res.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadFiles();
    loadStats();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    loadFiles();
  };

  const handleReset = () => {
    setSearchName('');
    setSearchExt('');
    setPage(1);
    loadFiles();
  };

  const handleDelete = async (id: string) => {
    try {
      await fileService.deleteFile(id);
      message.success('删除成功');
      loadFiles();
      loadStats();
    } catch {
      message.error('删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的文件');
      return;
    }
    try {
      await fileService.batchDeleteFiles(selectedRowKeys as string[]);
      message.success('批量删除成功');
      setSelectedRowKeys([]);
      loadFiles();
      loadStats();
    } catch {
      message.error('批量删除失败');
    }
  };

  const handlePreview = (record: fileService.FileRecord) => {
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    if (imageExts.includes(record.ext.toLowerCase())) {
      setPreviewUrl(record.url);
      setPreviewVisible(true);
    } else {
      window.open(record.url, '_blank');
    }
  };

  const handleDownload = async (record: fileService.FileRecord) => {
    try {
      await fileService.downloadFile(record.id, record.name);
    } catch {
      message.error('下载失败');
    }
  };

  const getFileIcon = (ext: string) => {
    const lowerExt = ext.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(lowerExt)) {
      return <FileImageOutlined style={{ color: '#52c41a' }} />;
    }
    if (lowerExt === 'pdf') {
      return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    }
    if (['doc', 'docx'].includes(lowerExt)) {
      return <FileWordOutlined style={{ color: '#1890ff' }} />;
    }
    if (['xls', 'xlsx'].includes(lowerExt)) {
      return <FileExcelOutlined style={{ color: '#52c41a' }} />;
    }
    if (['zip', 'rar', '7z'].includes(lowerExt)) {
      return <FileZipOutlined style={{ color: '#faad14' }} />;
    }
    return <FileOutlined />;
  };

  const uploadProps: UploadProps = {
    name: 'file',
    showUploadList: false,
    customRequest: async (options) => {
      const { file, onSuccess, onError } = options;
      try {
        await fileService.uploadFile(file as File);
        message.success('上传成功');
        onSuccess?.(null);
        loadFiles();
        loadStats();
      } catch (err: any) {
        message.error(err.response?.data?.message || '上传失败');
        onError?.(err);
      }
    },
  };

  const columns: ColumnsType<fileService.FileRecord> = [
    {
      title: '文件名',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (name, record) => (
        <Space>
          {getFileIcon(record.ext)}
          <span>{name}</span>
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'ext',
      key: 'ext',
      width: 70,
      render: (ext) => <Tag>{ext.toUpperCase()}</Tag>,
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (size) => fileService.formatFileSize(size),
    },
    {
      title: '存储类型',
      dataIndex: 'storageType',
      key: 'storageType',
      width: 100,
      render: (type) => {
        const opt = fileService.storageTypeOptions.find((o) => o.value === type);
        return opt?.label || type;
      },
    },
    { title: '上传者', dataIndex: 'uploadUsername', key: 'uploadUsername', width: 100 },
    {
      title: '上传时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (time) => time?.replace('T', ' ').substring(0, 19),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size={0} separator={<span style={{ color: '#d9d9d9' }}>|</span>}>
          <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => handlePreview(record)}>
            预览
          </Button>
          <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => handleDownload(record)}>
            下载
          </Button>
          <Popconfirm title="确定删除此文件？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger style={{ padding: '0 4px' }}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <Statistic title="文件总数" value={stats.totalCount} suffix="个" />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="存储空间" value={fileService.formatFileSize(stats.totalSize)} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="文件类型" value={stats.typeStats?.length || 0} suffix="种" />
          </Card>
        </Col>
      </Row>

      <Card
        title="文件管理"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => { loadFiles(); loadStats(); }}>刷新</Button>
            <Upload {...uploadProps}>
              <Button type="primary" icon={<UploadOutlined />}>上传文件</Button>
            </Upload>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="文件名"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            style={{ width: 150 }}
            allowClear
          />
          <Input
            placeholder="文件类型"
            value={searchExt}
            onChange={(e) => setSearchExt(e.target.value)}
            style={{ width: 100 }}
            allowClear
          />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button onClick={handleReset}>重置</Button>
          {selectedRowKeys.length > 0 && (
            <Popconfirm title={`确定删除选中的 ${selectedRowKeys.length} 个文件？`} onConfirm={handleBatchDelete}>
              <Button danger icon={<DeleteOutlined />}>
                批量删除 ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
        </Space>

        <Table
          columns={columns}
          dataSource={files}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      <Image
        style={{ display: 'none' }}
        preview={{
          open: previewVisible,
          src: previewUrl,
          onOpenChange: (open) => setPreviewVisible(open),
        }}
      />
    </div>
  );
};

export default FileManagement;
